// Inline survey — a multi-question structured ask. Pure helpers shared by the chat state
// (use-chat), the BFF history mapping (messages API), and the survey UI
// (MessageRow/SurveyWidget). The wire shape is the harness contract, and the types live in
// common-lib: an ASSISTANT row carries `metadata.survey`; the answering USER turn carries
// `metadata.surveyAnswer` referencing the ask, and the survey also rides `MessageEndEvent`
// in-band as field `survey`. A single-question ask stays on `OfferChoices`/`choices`; a
// survey holds two or more questions.
//
// Imported as TYPES ONLY, so this module (and its jest test) never drags common-lib's runtime
// graph — the `import type` is fully erased at transpile time.
import type {
  MessageSurvey,
  QuestionAnswer,
  SurveyAnswer,
  SurveyOption,
  SurveyQuestion,
} from '@wix/ai-scheduling-assistant-common-lib/entities';

export type { MessageSurvey, QuestionAnswer, SurveyAnswer, SurveyOption, SurveyQuestion };

function sanitizeOption(raw: unknown): SurveyOption | undefined {
  const { id, label, description } = (raw ?? {}) as { id?: unknown; label?: unknown; description?: unknown };
  if (typeof id !== 'string' || !id || typeof label !== 'string' || !label) return undefined;
  return { id, label, ...(typeof description === 'string' && description ? { description } : {}) };
}

function sanitizeQuestion(raw: unknown): SurveyQuestion | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  // `allowFreeText` is deliberately NOT read off the row — see the return below.
  const { id, prompt, options, multiSelect } = raw as {
    id?: unknown;
    prompt?: unknown;
    options?: unknown;
    multiSelect?: unknown;
  };
  if (typeof id !== 'string' || !id || typeof prompt !== 'string' || !prompt) return undefined;
  const clean: SurveyOption[] = [];
  if (Array.isArray(options)) {
    for (const o of options) {
      const option = sanitizeOption(o);
      if (option) clean.push(option);
    }
  }
  // Every question ends with a write-your-own field, whatever the row says. The harness forces
  // `allowFreeText` on every question it mints (offer-survey-tool), so the stored flag can only ever
  // disagree by being OLDER than that rule — a survey persisted before it, or one from a harness still
  // rolling out — and honouring it there would hand the reader a page of options with no way past
  // them. Nothing downstream reads the flag as the model's opinion either: `buildSurveyAnswer` drops
  // typed text for a question whose `allowFreeText` is false, so the field and the answer it produces
  // have to come from the same decision or the card collects text it then throws away.
  //
  // A question with no options at all is now the degraded shape rather than the open-ended one (the
  // tool rejects it), but it stays answerable through the free-text field, so it still renders.
  return { id, prompt, options: clean, multiSelect: multiSelect === true, allowFreeText: true };
}

/**
 * Read + sanitize `metadata.survey` off a raw message entity: a valid askId and at least one
 * well-formed question, else undefined. Fail-closed so a malformed survey renders nothing rather
 * than a broken card. (One question is a shape the harness mints in its own right — a lone question
 * with more suggested answers than a chip row can carry — so a single-question card is normal here,
 * not a degraded payload.)
 */
export function readMessageSurvey(metadata: unknown): MessageSurvey | undefined {
  const raw = (metadata as { survey?: unknown } | undefined)?.survey;
  if (!raw || typeof raw !== 'object') return undefined;
  const { askId, questions } = raw as { askId?: unknown; questions?: unknown };
  if (typeof askId !== 'string' || !askId || !Array.isArray(questions)) return undefined;
  const clean: SurveyQuestion[] = [];
  for (const q of questions) {
    const question = sanitizeQuestion(q);
    if (question) clean.push(question);
  }
  return clean.length > 0 ? { askId, questions: clean } : undefined;
}

function sanitizeQuestionAnswer(raw: unknown): QuestionAnswer | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const { questionId, optionIds, freeText } = raw as {
    questionId?: unknown;
    optionIds?: unknown;
    freeText?: unknown;
  };
  if (typeof questionId !== 'string' || !questionId) return undefined;
  const ids = Array.isArray(optionIds) ? optionIds.filter((x): x is string => typeof x === 'string' && !!x) : [];
  const text = typeof freeText === 'string' && freeText.trim() ? freeText : undefined;
  // An answer with nothing chosen and nothing typed carries no information — omit it.
  if (ids.length === 0 && !text) return undefined;
  return { questionId, optionIds: ids, ...(text ? { freeText: text } : {}) };
}

/** Read + sanitize `metadata.surveyAnswer` off a raw message entity. */
export function readSurveyAnswer(metadata: unknown): SurveyAnswer | undefined {
  const raw = (metadata as { surveyAnswer?: unknown } | undefined)?.surveyAnswer;
  if (!raw || typeof raw !== 'object') return undefined;
  const { askId, answers } = raw as { askId?: unknown; answers?: unknown };
  if (typeof askId !== 'string' || !askId || !Array.isArray(answers)) return undefined;
  const clean: QuestionAnswer[] = [];
  for (const a of answers) {
    const answer = sanitizeQuestionAnswer(a);
    if (answer) clean.push(answer);
  }
  return clean.length > 0 ? { askId, answers: clean } : undefined;
}

type SurveyRow = { role?: string; surveyAnswer?: SurveyAnswer };

/**
 * The submitted answer for an ask, derived from the transcript: the first USER row carrying a
 * `surveyAnswer` matching this ask. Covers the optimistic bubble (stamped locally on submit) and
 * persisted history alike. A survey is answered once, so unlike choices there is a single row.
 */
export function submittedSurveyAnswer(messages: readonly SurveyRow[], askId: string): SurveyAnswer | undefined {
  for (const m of messages) {
    if (m.role === 'USER' && m.surveyAnswer?.askId === askId) return m.surveyAnswer;
  }
  return undefined;
}

/**
 * The ask the user is being put to RIGHT NOW — the one a surface may stand in place of its composer,
 * rather than merely draw in the transcript.
 *
 * The scan walks BACK from the newest row and **stops at the first USER turn**: only an ask the user
 * has not yet spoken past is current. Walking further finds the newest survey anywhere in history —
 * including one the conversation moved on from ten turns ago — and a surface that hands that one the
 * composer has taken the input hostage behind a live conversation. The state is reachable without
 * any surface offering it: an inline card on either phone can simply be typed past, and transcripts
 * written before the composer slot existed already contain such rows. An ask answered in prose is
 * answered, and the transcript saying so IS the user turn.
 *
 * `dismissedAskIds` is the caller's own session state (X / Skip / a nothing-answered submit). An ask
 * already answered — or dismissed — does not fall through to an older one: it is still the current
 * ask, it is simply settled, and reaching past it would resurrect a question the user just closed.
 */
export function pendingSurveyAsk<T extends SurveyRow & { survey?: MessageSurvey }>(
  messages: readonly T[],
  dismissedAskIds?: ReadonlySet<string>,
): { message: T; survey: MessageSurvey } | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role === 'USER') return undefined;
    if (!m.survey) continue;
    if (submittedSurveyAnswer(messages, m.survey.askId)) return undefined;
    if (dismissedAskIds?.has(m.survey.askId)) return undefined;
    return { message: m, survey: m.survey };
  }
  return undefined;
}

/**
 * Has any row actually taken delivery of this ask?
 *
 * The question only arises because an ask now reaches the client BEFORE any row carries it (the
 * harness hands it over as soon as the model calls OfferSurvey — see `earlySurvey` in use-chat). Most
 * of those land: the turn stamps the survey on its reply and the row takes ownership. Some never do —
 * chips outrank a survey on one reply, and the harness's terminal-ask guard drops an offer that real
 * work followed — and for those the ask is one the TURN abandoned.
 *
 * That matters at exactly one seam: an answer the reader submitted while the turn was still streaming
 * is held until the stream closes (`send` no-ops mid-stream), and flushing it unconditionally would
 * send a `surveyAnswer` naming an askId no row ever carried — an answer to a question the conversation
 * decided not to ask, which nothing downstream can match back to anything.
 *
 * Deliberately NOT `pendingSurveyAsk`: by the time the answer is flushed the ask is about to be
 * answered, and an already-dismissed or answered ask is still an ask that LANDED. The question here is
 * only whether it exists on the record at all.
 */
export function surveyAskOnARow(messages: readonly { survey?: MessageSurvey }[], askId: string): boolean {
  return messages.some((m) => m.survey?.askId === askId);
}

/**
 * Selection derives from the transcript, mirroring `selectedOptionIds`: the option ids chosen for
 * one question of one ask, read off the USER row's `surveyAnswer`.
 */
export function selectedSurveyOptionIds(
  messages: readonly SurveyRow[],
  askId: string,
  questionId: string,
): Set<string> {
  const ids = new Set<string>();
  const answer = submittedSurveyAnswer(messages, askId);
  if (!answer) return ids;
  for (const a of answer.answers) {
    if (a.questionId === questionId) a.optionIds.forEach((id) => ids.add(id));
  }
  return ids;
}

/** The per-question working state a survey card holds while the user fills it in. */
export type SurveyDraft = {
  optionIds: Record<string, readonly string[]>;
  freeText: Record<string, string>;
};

/**
 * Build ONE `SurveyAnswer` from a filled-in draft. For each question with at least one selected
 * (and still-valid) option OR free text that is not blank, push a `{ questionId, optionIds, freeText? }`
 * — the free text kept EXACTLY as typed (trimming only decides whether it counts as answered),
 * omitted when blank. Fully-empty questions are omitted. Returns null when nothing was answered —
 * the caller treats that as Skip and sends nothing.
 */
export function buildSurveyAnswer(survey: MessageSurvey, draft: SurveyDraft): SurveyAnswer | null {
  const answers: QuestionAnswer[] = [];
  for (const q of survey.questions) {
    const optionIds = (draft.optionIds[q.id] ?? []).filter((id) => q.options.some((o) => o.id === id));
    // Trim decides EMPTINESS only; what we send is what the user typed, verbatim — the same rule
    // `sanitizeQuestionAnswer` reads with, so a round-trip through the wire is the identity.
    // Typed text counts on every question, gated on nothing — the card shows the field on every page
    // (SurveyWidget), and a builder that dropped the text for one of them would discard words the user
    // watched themselves type. The field and the answer have to come from the same decision.
    const raw = draft.freeText[q.id] ?? '';
    const text = raw.trim() ? raw : '';
    if (optionIds.length === 0 && !text) continue;
    answers.push({ questionId: q.id, optionIds: [...optionIds], ...(text ? { freeText: text } : {}) });
  }
  return answers.length > 0 ? { askId: survey.askId, answers } : null;
}

/**
 * The readable user-bubble text sent alongside a `surveyAnswer`, exactly as a tapped chip's label
 * rides `send`. Answered questions joined by "\n"; each line is `${prompt}: ${answerText}`, where
 * answerText is the selected option labels joined by ", " with free text appended (", "-separated
 * when options were also chosen, else the free text alone).
 */
export function composeSurveyMessageText(survey: MessageSurvey, answer: SurveyAnswer): string {
  const lines: string[] = [];
  for (const qa of answer.answers) {
    const q = survey.questions.find((x) => x.id === qa.questionId);
    if (!q) continue;
    const parts = qa.optionIds
      .map((id) => q.options.find((o) => o.id === id)?.label)
      .filter((l): l is string => !!l);
    if (qa.freeText) parts.push(qa.freeText);
    lines.push(`${q.prompt}: ${parts.join(', ')}`);
  }
  return lines.join('\n');
}

type SurveyableRow = { id?: string; finalId?: string; survey?: MessageSurvey };

/** Patch the matching row's survey in place (matched by id OR persisted finalId); no-op when the row is gone. */
export function patchSurveyById<M extends SurveyableRow>(messages: M[], messageId: string, survey: MessageSurvey): M[] {
  const matches = (m: M) => m.id === messageId || m.finalId === messageId;
  if (!messages.some(matches)) return messages;
  return messages.map((m) => (matches(m) ? { ...m, survey } : m));
}
